import { EllipsisVerticalIcon, LockClosedIcon } from '@heroicons/react/24/outline';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import React, { useEffect } from 'react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@dashboard/components/ui/dropdown-menu';
import Spinner from '@dashboard/components/Spinner';
import { Button } from '@dashboard/components/ui/button';
import { useAuth } from '@dashboard/context/AuthContext/AuthContext';
import useSellerQueries from '@dashboard/hooks/queries/useSellerQueries';
import Form from '@dashboard/components/Formik/Form';
import { Badge } from '@dashboard/components/ui/badge';
import Tooltip from '@dashboard/components/Tooltip';
import { toast } from '@dashboard/context/ToastContext';
import SettingsDivider from '@dashboard/components/SettingsDivider';
import { teamMembersKey, teamInvitesKey } from '@dashboard/lib/queryKeys';
import { confirm } from '@dashboard/lib/confirm';
import SettingsDescription from '@dashboard/components/SettingsDescription';
import SettingsHeader from '@dashboard/components/SettingsHeader';
import SettingsContainer from '@/dashboard/components/SettingsContainer';
import PageHeader from '@dashboard/components/PageHeader';
import { upperFirst } from 'lodash';

const SettingsMembers = () => {
  const { isOwner, isFree, verifyAuth, isAdminLoggedInAsSeller } = useAuth();

  // refresh auth to ensure data is accurate
  useEffect(() => {
    if (isAdminLoggedInAsSeller) return;

    verifyAuth();
  }, []);

  return (
    <div className="p-4 space-y-4">
      <PageHeader
        title="Team members"
        description="These are the people who have access to your account. Team owners can add more team members and remove existing ones."
      />

      <div className="mb-32">
        <SettingsContainer size="lg">
          <Members />

          {isOwner && (
            <>
              <PendingInvites />

              <SettingsDivider />

              <SettingsHeader>Invite team members</SettingsHeader>

              {isFree ? (
                <div className="rounded-md border border-input p-4 flex items-start gap-3">
                  <LockClosedIcon className="size-5 text-muted-foreground shrink-0 mt-0.5" />
                  <div className="space-y-3">
                    <div>
                      <p className="text-sm font-medium text-black dark:text-foreground">
                        Team members are available on paid plans
                      </p>
                      <p className="text-sm text-gray-700 dark:text-muted-foreground">
                        The Free plan includes a single team member. Upgrade to invite your team.
                      </p>
                    </div>
                    <Button to="/upgrade?upgradeFlow=team-members" size="sm">
                      Upgrade
                    </Button>
                  </div>
                </div>
              ) : (
                <>
                  <SettingsDescription>
                    Invite a new team member to your account.
                  </SettingsDescription>

                  <InviteMember />
                </>
              )}
            </>
          )}
        </SettingsContainer>
      </div>
    </div>
  );
};

function Members() {
  const queryClient = useQueryClient();
  const {
    seller,
    isOwner,
    userId,
    verifyAuth,
    isAdminLoggedInAsSeller,
    setSeller,
    availableStores,
    switchStore,
    updateAuthContext,
    user,
  } = useAuth();

  const { getMembers, deleteMember, changeAccountOwner } = useSellerQueries();

  const { data: members, isLoading: isLoadingMembers } = useQuery({
    queryKey: teamMembersKey,
    queryFn: () => getMembers(seller._id),
    enabled: !!seller._id,
  });

  const deleteMemberMutation = useMutation({
    mutationFn: deleteMember,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: teamMembersKey });
    },
  });

  const changeAccountOwnerMutation = useMutation({
    mutationFn: changeAccountOwner,
    onSuccess: data => {
      queryClient.invalidateQueries({ queryKey: teamMembersKey });
      setSeller(data);

      if (!isAdminLoggedInAsSeller) {
        verifyAuth();
      }
    },
  });

  if (isLoadingMembers) {
    return (
      <div>
        <Spinner />
      </div>
    );
  }

  const isAccountOwner = (isOwner && userId === seller.owner) || isAdminLoggedInAsSeller;

  return (
    <div>
      <ul role="list" className="divide-y divide-gray-100 dark:divide-border">
        {members?.map(member => (
          <li key={member._id} className="flex py-4">
            <div>
              <div className="font-medium text-black dark:text-foreground space-x-1 text-sm">
                <span>
                  {member.user.name}
                  {member.user._id === userId && (
                    <span className="text-gray-700 dark:text-muted-foreground text-sm"> (You)</span>
                  )}
                </span>

                <Tooltip
                  label={
                    member.role === 'owner'
                      ? 'The team owner can perform all actions'
                      : 'Admin users can perform all actions except managing team members'
                  }
                >
                  <Badge variant="secondary">{upperFirst(member.role)}</Badge>
                </Tooltip>
              </div>
              <p className="text-gray-700 dark:text-muted-foreground text-sm">
                {member.user.email}
              </p>
            </div>
            <div className="ml-auto relative">
              {((isOwner && member.role !== 'owner') ||
                (member.user._id === userId && !isOwner)) && (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" size="icon-sm" aria-label="More options">
                      <EllipsisVerticalIcon
                        className="size-5 text-muted-foreground"
                        aria-hidden="true"
                      />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem
                      variant="destructive"
                      onSelect={async () => {
                        const proceed = await confirm(
                          member.user._id === userId
                            ? `Leave your team?`
                            : `Remove ${member.user.name} from your team?`,
                        );

                        if (!proceed) return;

                        await deleteMemberMutation.mutateAsync({
                          sellerId: seller._id,
                          userId: member.user._id,
                        });

                        if (member.user._id === userId) {
                          const otherStores = availableStores.filter(
                            store => store._id !== seller._id,
                          );

                          if (otherStores.length > 0) {
                            await switchStore(otherStores[0]._id);
                          } else {
                            updateAuthContext({
                              user: { ...user, currentSeller: null },
                              seller: {},
                            });
                          }
                        }
                      }}
                    >
                      {member.user._id === userId ? 'Leave team' : 'Remove from team'}
                    </DropdownMenuItem>

                    {isAccountOwner && (
                      <DropdownMenuItem
                        onSelect={async () => {
                          const proceed = await confirm(
                            `Transfer ownership of your account to ${member.user.name} (${member.user.email})?`,
                            {
                              description: `You cannot undo this action.`,
                            },
                          );

                          if (!proceed) return;

                          changeAccountOwnerMutation.mutate({
                            sellerId: seller._id,
                            userId: member.user._id,
                          });
                        }}
                      >
                        Make account owner
                      </DropdownMenuItem>
                    )}
                  </DropdownMenuContent>
                </DropdownMenu>
              )}
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}

function formatTimeAgo(date) {
  const now = new Date();
  const diffMs = now - new Date(date);
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  if (diffDays === 0) return 'today';
  if (diffDays === 1) return '1 day ago';
  return `${diffDays} days ago`;
}

function formatExpiresIn(date) {
  const now = new Date();
  const diffMs = new Date(date) - now;
  const diffDays = Math.ceil(diffMs / (1000 * 60 * 60 * 24));

  if (diffDays <= 0) return 'Expired';
  if (diffDays === 1) return 'Expires tomorrow';
  return `Expires in ${diffDays} days`;
}

function PendingInvites() {
  const queryClient = useQueryClient();
  const { seller } = useAuth();

  const { getInvites, revokeInvite, resendInvite } = useSellerQueries();

  const { data: invites } = useQuery({
    queryKey: teamInvitesKey,
    queryFn: () => getInvites(seller._id),
    enabled: !!seller._id,
  });

  const revokeMutation = useMutation({
    mutationFn: revokeInvite,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: teamInvitesKey });
      toast({ title: 'Invite revoked', type: 'success' });
    },
  });

  const resendMutation = useMutation({
    mutationFn: resendInvite,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: teamInvitesKey });
      toast({
        title: 'Invite resent!',
        description: 'A new invite email has been sent',
        type: 'success',
      });
    },
    onError: error => {
      toast({
        title: "Couldn't resend the invite",
        description: error?.message,
        type: 'error',
      });
    },
  });

  if (!invites?.length) return null;

  return (
    <>
      <SettingsDivider />

      <SettingsHeader>Pending invites</SettingsHeader>
      <SettingsDescription>
        Invites that haven't been accepted yet. Invites expire after 7 days.
      </SettingsDescription>

      <ul role="list" className="divide-y divide-gray-100 dark:divide-border">
        {invites.map(invite => (
          <li key={invite._id} className="flex py-4">
            <div>
              <div className="font-medium text-black dark:text-foreground space-x-1 text-sm">
                <span>{invite.email}</span>
                <Badge variant="secondary">{upperFirst(invite.role)}</Badge>
                <Badge
                  variant="secondary"
                  className={
                    invite.status === 'pending'
                      ? '!bg-yellow-50 dark:!bg-yellow-500/15 !text-yellow-700 dark:!text-yellow-400'
                      : '!bg-gray-100 dark:!bg-muted !text-gray-500 dark:!text-muted-foreground'
                  }
                >
                  {invite.status === 'pending' ? 'Pending' : 'Expired'}
                </Badge>
              </div>
              <p className="text-gray-500 dark:text-muted-foreground text-sm">
                Sent {formatTimeAgo(invite.createdAt)}
                {invite.status === 'pending' && invite.expiresAt && (
                  <span> · {formatExpiresIn(invite.expiresAt)}</span>
                )}
              </p>
            </div>
            <div className="ml-auto relative">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="icon-sm" aria-label="More options">
                    <EllipsisVerticalIcon
                      className="size-5 text-muted-foreground"
                      aria-hidden="true"
                    />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem
                    onSelect={() => {
                      resendMutation.mutate({
                        sellerId: seller._id,
                        inviteId: invite._id,
                      });
                    }}
                  >
                    Resend invite
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    variant="destructive"
                    onSelect={async () => {
                      const proceed = await confirm(`Revoke the invite for ${invite.email}?`);
                      if (!proceed) return;

                      revokeMutation.mutate({
                        sellerId: seller._id,
                        inviteId: invite._id,
                      });
                    }}
                  >
                    Revoke invite
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </li>
        ))}
      </ul>
    </>
  );
}

export function InviteMember() {
  const queryClient = useQueryClient();
  const { seller } = useAuth();

  const { inviteMember } = useSellerQueries();

  const invite = useMutation({
    mutationFn: inviteMember,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: teamInvitesKey });
      toast({
        title: 'Invite sent!',
        description: 'Ask your team member to check their email',
        type: 'success',
      });
    },
  });

  return (
    <div>
      <Form
        initialValues={{ email: '', role: 'admin' }}
        onSubmit={async (values, actions) => {
          try {
            const response = await invite.mutateAsync({
              sellerId: seller._id,
              email: values.email,
            });

            actions.resetForm();

            return {
              data: response.data,
            };
          } catch (err) {
            return {
              error: err.message,
            };
          }
        }}
        fields={[
          {
            name: 'email',
            label: 'Email',
            type: 'email',
            placeholder: 'Enter email address',
            required: true,
          },
        ]}
        showState={false}
        submitButton={{
          label: 'Invite',
          appearance: 'primary',
        }}
      />
    </div>
  );
}

export default SettingsMembers;
